import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import {
  selectDatepicker,
  selectSelectedDate,
} from "../datepicker/datepickerSlice";
import { getBackendDateFormat, mergeUniqueByKey } from "../../common/utils";
import { areEventsFetched, areEventsNotScheduled } from "./utils";

export const getEvents = createAsyncThunk(
  "events/get",
  async (params, { getState, dispatch, rejectWithValue }) => {
    const selectedDate = params?.date || selectSelectedDate(getState());

    if (params?.ignoreCondition) {
      dispatch(eventsActions.resetEvents());
    }

    return api
      .get(`sessions/?day=${getBackendDateFormat(selectedDate)}`)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }

      const { data } = selectEvents(getState());
      const selectedDate = params?.date || selectSelectedDate(getState());
      const datePicker = selectDatepicker(getState());

      if (
        areEventsFetched(data, selectedDate) ||
        areEventsNotScheduled(
          datePicker.status === "fulfilled",
          datePicker.events,
          datePicker.competitions,
          selectedDate
        )
      ) {
        return false;
      }
    },
  }
);

const initialState = {
  status: "idle",
  data: [],
  error: null,
  view: "collective",
  expanded: [],
  selected: {
    action: null, // ---> "delete" | "add" | "edit" | "copy"
    item: {},
  },
};

const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    changeView: (state, action) => {
      state.view = action.payload;
      state.expanded = [];
    },
    expand: (state, action) => {
      if (state.expanded.includes(action.payload)) {
        state.expanded = state.expanded.filter((d) => d !== action.payload);
      } else {
        state.expanded.push(action.payload);
      }
    },
    resetExpand: (state) => {
      state.expanded = [];
    },
    setSelectedEvent: (state, action) => {
      state.selected.item = action.payload;
    },
    changeEventAction: (state, action) => {
      state.selected.action = action.payload;
    },
    add: (state, action) => {
      // ne pas ajouter si les données ont déjà été ajoutées
      const merged = mergeUniqueByKey(state.data, [action.payload]);
      state.data = merged;
    },
    set: (state, action) => {
      state.data = state.data.map((d) =>
        d.id === action.payload.id ? action.payload : d
      );
    },
    remove: (state, action) => {
      state.data = state.data.filter((d) => d.id !== action.payload);
    },
    resetEvents: (state) => {
      state.data = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEvents.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        state.status = "fulfilled";
        // ne pas ajouter si les données ont déjà été ajoutées
        const merged = mergeUniqueByKey(state.data, action.payload);
        state.data = merged;
      })
      .addCase(getEvents.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
      })
      .addCase("datepicker/changeDate", (state) => {
        state.expanded = [];
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const eventsActions = eventsSlice.actions;

const _selectEvents = (state) => state.events;

export const selectEvents = createSelector(
  [(state) => _selectEvents(state)],
  (events) => {
    const data = events.data;
    const status = events.status;
    const error = events.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

export const selectEventsView = (state) => state.events.view;

export const selectEventsExpanded = (state) => state.events.expanded;

export const selectMode = (state) => state.events.mode;

export const selectSelectedEvent = (state) => state.events.selected.item;

export const selectSelectedEventAction = (state) =>
  state.events.selected.action;

export default eventsSlice.reducer;
